import {
	Component,
	EventEmitter,
	HostListener,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Renderer2,
	ViewChild
} from '@angular/core';
import {
	ActionList,
	ClientPortalLink,
	QuestionnaireData
} from '@models/models';
import { QuestionnairesDataProvider } from '@services/questionnarie/questionnaires.data.service';
import { Subject, Subscription } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from '@services/helpers/message-service/message.service';
import { MailsDataProvider } from '@services/email/mails.data.service';
import { FileDownloadDataServiceProvider } from '@services/files/file-download-service/file-download.data.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'questionnaire',
	template: require('./questionnaire.html'),
	styles: [require('./questionnaire.scss')]
})
export class QuestionnaireComponent implements OnInit, OnDestroy {
	@ViewChild('frameRef') frameRef;

	@Input() jobId: string;
	@Input() questionnaireId: string;
	@Input() companyImageSrc: string;
	@Output() questionnaireSend: EventEmitter<void> = new EventEmitter();
	@Output() questionnaireDelete: EventEmitter<Object> = new EventEmitter();

	btnDisable: boolean;
	pdfSpinner: boolean;
	questionnaire: QuestionnaireData;
	actions: ActionList;
	frameLoaded: boolean;

	private frameLoadSuccessEvent;
	private modalDataSubscription: Subscription;
	private deleteModalRef: NgbModalRef;
	private unsubscribe: Subject<void> = new Subject();
	private qErrorEvent;

	constructor(
		private questionnairesDataService: QuestionnairesDataProvider,
		private messageService: MessageService,
		private mailsDataService: MailsDataProvider,
		private fileDownloadService: FileDownloadDataServiceProvider,
		private modalService: SNModalService,
		private renderer: Renderer2
	) {}

	ngOnInit(): void {
		this.getTemplateData(this.jobId, this.questionnaireId);
		this.listenToMailUpdate();
		this.listenToFrameDataLoad();
		this.listenToQuestionnaireV2Error();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		if (this.frameLoadSuccessEvent) {
			this.frameLoadSuccessEvent();
		}
		if (this.qErrorEvent) {
			this.qErrorEvent();
		}
	}

	previewQuestionnaire(): void {
		this.btnDisable = true;
		this.questionnairesDataService
			.getQuestionnairePreviewLink(
				this.questionnaire.actions.GET_CLIENT_PORTAL_LINK.url
			)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.btnDisable = false))
			)
			.subscribe(
				(data: ClientPortalLink): Window =>
					window.open(
						`clients/questionnaire/${data.link}/${this.questionnaire.id}`,
						'_blank'
					),
				(err): void => this.errorHandleQuestionnaire(err)
			);
	}

	emitQuestionnaireSend(): void {
		this.questionnaireSend.emit();
	}

	deleteQuestionnaire(): void {
		this.deleteModalRef = this.modalService.openModal(DeleteItemModalComponent);

		this.subscribeToModalData();

		this.deleteModalRef.componentInstance.modalTitle = 'Questionnaire';
		this.deleteModalRef.componentInstance.btnDisabled = false;
		this.deleteModalRef.componentInstance.itemName = this.questionnaire.name;
	}

	saveAsPdf(): void {
		this.pdfSpinner = true;
		this.fileDownloadService
			.downloadPDF(
				this.actions.SAVE_AS_PDF.url,
				this.questionnaire.name,
				this.questionnaire.id
			)
			.pipe(finalize((): boolean => (this.pdfSpinner = false)))
			.subscribe(
				(): void => {},
				(err): void => this.errorHandleQuestionnaire(err)
			);
	}

	private subscribeToModalData(): void {
		this.modalDataSubscription = this.modalService
			.getModalData()
			.pipe(filter((data): boolean => data && data.action))
			.subscribe((): void => this.deleteQuestionnaireAction());
	}

	private deleteQuestionnaireAction(): void {
		this.deleteModalRef.componentInstance.btnDisabled = true;
		this.questionnairesDataService
			.deleteQuestionnaireV2(this.actions.DELETE_QUESTIONNAIRE.url)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): void => {
					this.deleteModalRef.componentInstance.btnDisabled = false;
					this.modalDataSubscription.unsubscribe();
					this.deleteModalRef.close();
				})
			)
			.subscribe(
				(data): void => this.questionnaireDelete.emit(data),
				(err): void => this.errorHandleQuestionnaire(err)
			);
	}

	private getTemplateData(jobId: string, questionnaireId: string): void {
		this.questionnairesDataService
			.getQuestionnaireViewData(jobId, questionnaireId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(questionnaireData: QuestionnaireData): void =>
					this.handleTemplateResponse(questionnaireData),
				(err): void => this.errorHandleQuestionnaire(err)
			);
	}

	@HostListener('window:beforeprint', [])
	onBeforePrint(): void {
		this.frameRef.nativeElement.style.height = `${this.frameRef.nativeElement
			.offsetHeight + 200}px`;
	}

	private errorHandleQuestionnaire({ error }): void {
		const { errorMessage, errorTitle } = error;

		if (
			errorMessage.includes(
				'This questionnaire is linked to an active workflow task and cannot be deleted. Please delete the workflow task first.'
			)
		) {
			this.messageService.showToaster('e_2', 'questionnaires');
			return;
		}

		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}

	private listenToMailUpdate(): void {
		this.mailsDataService.shouldMailsBeUpdatedObservable
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((state: boolean): void => {
				if (state) {
					this.getTemplateData(this.jobId, this.questionnaireId);
				}
			});
	}

	private listenToFrameDataLoad(): void {
		this.frameLoadSuccessEvent = this.renderer.listen(
			'document',
			'QuestionnaireDataLoadSuccess',
			(): boolean => (this.frameLoaded = true)
		);
	}

	@HostListener('window:afterprint', [])
	onAfterPrint(): void {
		this.frameRef.nativeElement.style.height = `${this.frameRef.nativeElement
			.offsetHeight - 200}px`;
	}

	private handleTemplateResponse(questionnaireData: QuestionnaireData): void {
		this.questionnaire = questionnaireData;
		this.actions = questionnaireData.actions;
	}

	private listenToQuestionnaireV2Error(): void {
		this.qErrorEvent = this.renderer.listen(
			'document',
			'QuestionnaireValidationError',
			(data: CustomEvent): void => {
				const { errorTitle, errorMessage } = data.detail;
				this.messageService.showErrorFromBackend(errorTitle, errorMessage);
			}
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/questionnaire/questionnaire.ts